import * as React from 'react';
import { Button } from 'react-bootstrap';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect, useDispatch, useSelector, useStore } from 'react-redux';
import Select from 'react-select';
import { toastr } from 'react-redux-toastr';
import { constants } from '../../constants/constants';
import {
    getFSEAvailableJobsForWorkOrders,
    getFSEAvailableJobTypesForWorkOrders
} from '../../actions/manageDashboardActions';
import { IdashboardItem, Ifacility, Ijob } from '../../models';
import { IinitialState } from '../../reducers';
import SideModal from '../sideModal/SideModal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { saveJobOnline, addWorkOrdersToJobOnline } from '../../actions/manageJobActions';
import { selectNextJobNumber } from '../../reducers/manageJobReducer';
import { jobTypesIdEnumInverse } from '../../models-enums';
import { getUser } from '../../reducers/userReducer';
import { endAjaxCall } from '../../actions/ajaxStatusActions';
import { getDefaultJobNumber } from '../../utils/jobNumberUtils';

interface Option {
    value: string;
    label: string;
    data?: any;
}

import { v4 as uuidv4 } from 'uuid';

enum JobFlow {
    ExistingJob = 'existing-job',
    NewJob = 'new-job'
}

interface Iprops {
    visible: boolean;
    selectedItems: Array<IdashboardItem>;
    onUnderlayClick: () => void;
    onCancel: () => void;
    // Awaited by handleSubmit, so the Start button stays disabled until the job is actually open.
    // That window - job created, not yet open - is exactly when the FSE used to retry and create a
    // duplicate, and any failure needs to reach handleSubmit's catch rather than go unhandled.
    onStart: (jobId: string) => void | Promise<void>;
    selectedRow: IdashboardItem | undefined;
    selectedJobFacility?: Ifacility;
}

const DashboardSidebarCreate: React.FC<Iprops & WithTranslation> = props => {
    const { t, visible, selectedItems, onUnderlayClick, onCancel, onStart, selectedRow, selectedJobFacility } = props;

    const nextJobNumber = useSelector(selectNextJobNumber);
    const user = useSelector(getUser);
    const store = useStore();

    const [existingJob, setExistingJob] = React.useState<Option | null>(null);
    const [jobOptions, setJobOptions] = React.useState<Array<Option>>([]);
    const [jobTypeOptions, setJobTypeOptions] = React.useState<Array<Option>>([]);
    const [selectedJobType, setSelectedJobType] = React.useState<Option | null>(null);
    const [selectedFlow, setSelectedFlow] = React.useState<null | JobFlow>(JobFlow.NewJob);
    const [selectedJobNumber, setSelectedJobNumber] = React.useState<null | string>(null);
    const [newJobStartDate, setNewJobStartDate] = React.useState<null | Date>(new Date());
    const [newJobEndDate, setNewJobEndDate] = React.useState<null | Date>(new Date());
    const selectedItemsFacility = selectedItems.find(item => item)?.facility;
    const selectedFacilityName = selectedItemsFacility?.name ? selectedItemsFacility.name : null;
    const selectedJobFacilityName = selectedJobFacility?.name ? selectedJobFacility.name : null;
    const facilityName = selectedRow?.facility.name || selectedFacilityName || selectedJobFacilityName;
    const selectedFacilityID = selectedItemsFacility?.id ? selectedItemsFacility.id : null;
    const facilityID = selectedFacilityID ?? selectedRow?.facility.id;

    const dispatch = useDispatch();
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    React.useEffect(() => {
        const fetchJobsAndTypes = async () => {
            const workOrders =
                selectedItems.length > 0 ? selectedItems.map(x => x.workOrder) : [selectedRow?.workOrder];
            const countryID = (selectedItems.find(item => item)?.facility ?? selectedRow?.facility)?.countryID;
            setSelectedJobNumber(getDefaultJobNumber(countryID, workOrders, nextJobNumber));

            if (selectedItems.length === 0 && !selectedRow?.workOrder?.id) {
                console.error('No work orders selected');
                toastr.error('No work orders selected', 'Error', constants.toastrError);
                return;
            }

            let ids = selectedItems.map(x => x.workOrder?.id || '').filter(x => x);

            const selectedRowItemId = selectedRow?.workOrder?.id;

            if (ids.length === 0 && !selectedRowItemId) {
                console.error('No work order ids found for request');
                toastr.error('No work order ids found for request', 'Error', constants.toastrError);
                return;
            }

            if (ids.length === 0 && selectedRowItemId) {
                ids.push(selectedRowItemId);
            }

            const [jobsData, jobTypesData = []] = await Promise.all([
                getFSEAvailableJobsForWorkOrders(dispatch, ids),
                getFSEAvailableJobTypesForWorkOrders(dispatch, ids)
            ]);

            setJobOptions(jobsData.map((x: any) => ({ label: x.jobNumber, value: x.id, data: x })));
            setJobTypeOptions(
                jobTypesData.map((x: any) => ({
                    label: x.name,
                    value: x.id,
                    data: x
                }))
            );

            const defaultType = jobTypesData.find((x: any) => x.isDefault);
            if (defaultType) {
                const defaultTypeOption = { label: defaultType.name, value: defaultType.id, data: defaultType };
                setSelectedJobType(defaultTypeOption);
            }
        };

        if (visible) {
            fetchJobsAndTypes();
        }
    }, [visible, selectedItems, dispatch, selectedRow, nextJobNumber]);

    const reset = () => {
        setSelectedJobType(null);
    };

    const handleStartDateChange = (date: Date) => {
        setNewJobStartDate(date);
    };
    const handleEndDateChange = (date: Date) => {
        setNewJobEndDate(date);
    };

    const isNewJobFormDisabled = selectedFlow === JobFlow.ExistingJob || selectedFlow === null;

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);

        let workOrderIds = selectedItems.map(item => item.id);

        if (selectedItems.length === 0 && selectedRow && selectedRow?.workOrder) {
            workOrderIds = [selectedRow?.workOrder?.id];
        } else {
            console.info('No work orders selected');
        }

        if (selectedFlow === JobFlow.ExistingJob) {
            if (!existingJob) {
                setIsSubmitting(false);
                throw new Error('Existing job is not selected');
            }

            const jobId = existingJob.value;

            addWorkOrdersToJobOnline(workOrderIds, jobId, t, true, store.getState, dispatch)
                .then(() => {
                    return onStart(jobId);
                })
                .catch(error => {
                    console.error('Error adding work orders to job:', error);
                    dispatch(endAjaxCall());
                })
                .finally(() => {
                    setIsSubmitting(false);
                    dispatch(endAjaxCall());
                });
        } else if (selectedFlow === JobFlow.NewJob) {
            const formData = new FormData(e.currentTarget);
            const data = Object.fromEntries(formData.entries());
            const jobNumber = String(data['job-number'] ?? '');
            const startDate = new Date(String(data['start-date'] ?? '')).toISOString();
            const endDate = new Date(String(data['end-date'] ?? '')).toISOString();

            const jobId = uuidv4() as string;
            const jobTypeID = selectedJobType?.value as keyof typeof jobTypesIdEnumInverse;

            if (!facilityID) {
                setIsSubmitting(false);
                console.error('Missing facility id');
                throw new Error('Missing facility id');
            }

            const newJob: Ijob = {
                id: jobId,
                jobNumber,
                jobTypeID,
                facilityID,
                startDate,
                endDate,
                assignedUserID: user.id,
                status: 'New',
                jobHours: [],
                fseNotes: '',
                source: 0,
                isSAPClosed: false,
                isDeleted: false
            };

            saveJobOnline(newJob, t, [], jobId, dispatch)
                .then(() => {
                    if (jobId === null) {
                        throw new Error('JobId is required for addWorkOrdersToJobOnline');
                    }
                    return addWorkOrdersToJobOnline(workOrderIds, jobId, t, true, store.getState, dispatch);
                })
                .then(() => {
                    return onStart(jobId);
                })
                .catch(error => {
                    console.error('Error saving job:', error);
                    dispatch(endAjaxCall());
                })
                .finally(() => {
                    setIsSubmitting(false);
                    dispatch(endAjaxCall());
                });
        }
    };

    const isStartDisabled =
        isSubmitting ||
        selectedFlow === null ||
        (selectedFlow === JobFlow.ExistingJob && !existingJob) ||
        (selectedFlow === JobFlow.NewJob && !selectedJobType);

    const modalTitle = t('manageInventory:addWorkOrdersToJob', { ns: 'manageInventory' });

    return (
        <SideModal
            visible={visible}
            className="dashboard-sidebar-create"
            onUnderlayClick={() => {
                onUnderlayClick();
                reset();
            }}
        >
            <SideModal.Header>
                <strong>{modalTitle}</strong>
            </SideModal.Header>
            <SideModal.Body>
                <div>
                    <div>{t('manageInventory:workOrdersSelected')}</div>
                    {selectedItems.map(item => (
                        <div key={item.id} className="wo-line">
                            {item.workOrder?.number || '[n/a]'}
                            {item.orderType ? ` - ${item.orderType}` : ''}
                            {item.workOrder?.activityDescription ? ` - ${item.workOrder.activityDescription}` : ''}
                        </div>
                    ))}
                    {selectedItems.length === 0 && selectedRow && (
                        <div key={selectedRow.id} className="wo-line">
                            {selectedRow.workOrder?.number || '[n/a]'}
                            {selectedRow.orderType ? ` - ${selectedRow.orderType}` : ''}
                            {selectedRow.workOrder?.activityDescription
                                ? ` - ${selectedRow.workOrder.activityDescription}`
                                : ''}
                        </div>
                    )}
                </div>
                <div>
                    <div className="add-to-existing-job">
                        <div className="radio-label-container">
                            <input
                                type="radio"
                                id="add-to-existing-job"
                                name="add-or-create-job"
                                value={JobFlow.ExistingJob}
                                className="radio-input"
                                checked={selectedFlow === JobFlow.ExistingJob}
                                onChange={() => setSelectedFlow(JobFlow.ExistingJob)}
                            />
                            <label htmlFor="add-to-existing-job" className="radio-label">
                                {t('manageInventory:addToAnExistingJob')}
                            </label>
                        </div>
                        <Select
                            id="jobs"
                            placeholder="Search..."
                            classNamePrefix="react-select"
                            options={jobOptions}
                            isMulti={false}
                            isDisabled={selectedFlow === JobFlow.NewJob || selectedFlow === null}
                            onChange={option => {
                                if (!option) {
                                    return;
                                }

                                setExistingJob({ label: option.label, value: option.value, data: option });
                            }}
                        />
                    </div>
                    <p className="input-hint">{t('manageInventory:addWorkOrderToExistingJob')}</p>
                </div>

                <div className="create-new-job">
                    <form id="create-new-job-form" onSubmit={handleSubmit}>
                        <div className="radio-label-container">
                            <input
                                type="radio"
                                id="create-new-job"
                                name="add-or-create-job"
                                value={JobFlow.NewJob}
                                className="radio-input"
                                checked={selectedFlow === JobFlow.NewJob}
                                onChange={() => setSelectedFlow(JobFlow.NewJob)}
                            />
                            <label htmlFor="create-new-job" className="radio-label">
                                {t('manageInventory:createNewJob')}
                            </label>
                        </div>
                        <div className="form-group">
                            <label htmlFor="facility" className="input-label">
                                {t('nsJob:facility')}
                            </label>
                            <input
                                id="facility"
                                name="facility"
                                type="text"
                                className="input-text"
                                readOnly
                                value={facilityName ?? ''}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="job-number" className="input-label">
                                {t('nsJob:jobNumber')} -{' '}
                                <span className="input-label-flag">{t('common:optional')}</span>
                            </label>
                            <input
                                id="job-number"
                                name="job-number"
                                type="text"
                                className="input-text"
                                disabled={isNewJobFormDisabled}
                                onChange={e => {
                                    setSelectedJobNumber(e.target.value);
                                }}
                                value={selectedJobNumber ?? ''}
                                placeholder="Enter Job Number"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="job-type" className="input-label">
                                {t('nsJob:type')}
                            </label>
                            <Select
                                name="job-type"
                                inputId="job-type"
                                classNamePrefix="react-select"
                                options={jobTypeOptions}
                                isMulti={false}
                                isDisabled={isNewJobFormDisabled}
                                onChange={option => {
                                    setSelectedJobType(option);
                                }}
                                value={selectedJobType}
                            />
                        </div>
                        <div className="create-job-dates-container">
                            <div className="input-date">
                                <span>{t('nsJob:startDate')}:</span>
                                <DatePicker
                                    name="start-date"
                                    className="start-date"
                                    selected={new Date(newJobStartDate || '')}
                                    onChange={handleStartDateChange}
                                    dateFormat="dd-MMM-yy"
                                    disabled={isNewJobFormDisabled}
                                    portalId="datepicker-portal"
                                />
                            </div>
                            <div className="input-date">
                                <span>{t('nsJob:endDate')}:</span>
                                <DatePicker
                                    name="end-date"
                                    className="end-date"
                                    selected={new Date(newJobEndDate || '')}
                                    onChange={handleEndDateChange}
                                    dateFormat="dd-MMM-yy"
                                    disabled={isNewJobFormDisabled}
                                    portalId="datepicker-portal"
                                />
                            </div>
                        </div>
                    </form>
                </div>
            </SideModal.Body>
            <SideModal.Footer>
                <Button
                    type="button"
                    onClick={() => {
                        onCancel();
                        reset();
                    }}
                    disabled={isSubmitting}
                >
                    {t('common:cancel')}
                </Button>
                <Button form="create-new-job-form" type="submit" className="blue" disabled={isStartDisabled}>
                    {t('common:startJob')}
                </Button>
            </SideModal.Footer>
        </SideModal>
    );
};

const mapStateToProps = (state: IinitialState, ownProps: Iprops) => {
    return {
        selectedJobFacility: state.facilities.facilitiesByID[state.manageJob.selectedJob.facilityID]
    };
};

export default withTranslation('dashboard')(connect(mapStateToProps, {})(DashboardSidebarCreate));
